import React, { useState } from 'react';
import { Modal, notification, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Upload } from 'react-feather';
import Button from '../../../../common/Button';
import { openNotificationWithIcon, User } from '@mss/mss-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { StyledCompactTable } from '../../../../configuration/util/utils';

type Props = {
  closeModal: () => void;
};

type UploadResultAPI = {
  successful: User[];
  existing: string[];
  exceptions: string[];
};

type ResultStatus = 'success' | 'existing' | 'exception';

type UploadResult = Array<
  Partial<User> & {
    result: ResultStatus;
  }
>;

const statusColorMap: Record<ResultStatus, string> = {
  success: 'bg-success',
  existing: 'bg-warning',
  exception: 'bg-danger',
};

const statusTextMap: Record<ResultStatus, string> = {
  success: 'User successful added',
  existing: 'User already exists',
  exception: 'Cannot add user',
};

const uploadResultMapper = (uploadResult: UploadResultAPI): UploadResult => [
  ...uploadResult.exceptions.map(x => ({
    email: x.replace(/"/g, ''),
    result: 'exception' as ResultStatus,
  })),
  ...uploadResult.existing.map(x => ({
    email: x.replace(/"/g, ''),
    result: 'existing' as ResultStatus,
  })),
  ...uploadResult.successful.map(x => ({
    ...x,
    email: x.email.replace(/"/g, ''),
    result: 'success' as ResultStatus,
  })),
];

const ImportModal = ({ closeModal }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadResults, setUploadResults] = useState<UploadResult | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (selectedFile) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('csv_file', selectedFile);

      try {
        const response = await fetch('/api/user/csv_upload/', {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${window.sessionStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `${
              (await response.json?.()) ||
              (await response.text?.()) ||
              response.statusText
            }`
          );
        }

        const result = await response.json();
        setUploadResults(uploadResultMapper(result));
        setSelectedFile(null);
        openNotificationWithIcon(
          notification.success,
          t('File uploaded successfully.'),
          null
        );
        if (result.successful.length > -1)
          await queryClient.invalidateQueries({ queryKey: ['users'] });
      } catch (error) {
        const errorMsg = !String(error).includes('Incorrect format')
          ? "Due to technical difficulties we can't process your file. Please contact your administrator."
          : 'The file was incorrectly formatted, please consult the example file and try again.';
        openNotificationWithIcon(
          notification.error,
          t('File upload failed'),
          t(errorMsg)
        );
      } finally {
        setIsUploading(false);
      }
    }
  };
  return (
    <Modal
      width={600}
      onCancel={closeModal}
      visible
      title={t('Import users')}
      footer={
        <div>
          {!uploadResults && (
            <Button
              variant={'primary'}
              disabled={!selectedFile}
              onClick={handleFileUpload}
              loading={isUploading}
            >
              <Upload size={16} className={'mr-2'} />
              <>{t('Upload file')}</>
            </Button>
          )}
          {uploadResults && (
            <Button
              ghost
              variant={'primary'}
              onClick={() => setUploadResults(null)}
            >
              {t('Import more users')}
            </Button>
          )}
          <Button ghost variant={'primary'} onClick={closeModal}>
            {t('Close')}
          </Button>
        </div>
      }
    >
      {!uploadResults && (
        <div className={isUploading ? 'opacity-50' : ''}>
          <p>
            {t(
              'Here you can upload a CSV file containing emails from the users. Please keep in mind that these users need to be present in Keycloak.'
            )}
          </p>
          <p>
            <a
              href="/example_user_import.csv"
              download="example_user_import.csv"
              className="underline text-primary hover:font-bold"
            >
              {t('An example CSV can be found here.')}
            </a>
          </p>
          <input
            id="file"
            className="hidden"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
          />
          <label
            htmlFor="file"
            className="cursor-pointer inline-block w-1/4 border border-primary bg-white text-primary hover:bg-primary hover:text-white transition-colors duration-300 text-center px-4 py-1"
          >
            {t('Select file')}
          </label>
          <div className="inline-block w-3/4 pl-4 font-bold">
            {selectedFile?.name}
          </div>
        </div>
      )}
      {uploadResults && (
        <>
          <h1>{t('Upload results')}</h1>
          <p>
            {t(
              'Please review the results below. The failed results are listed on top.'
            )}
          </p>
          <p>
            <ul>
              {Object.entries(statusColorMap).map(([status, bgClass]) => (
                <li key={status}>
                  <span
                    className={`inline-block mr-3 w-2 h-2 rounded-full ${bgClass}`}
                  />
                  {t(statusTextMap[status as ResultStatus])}
                </li>
              ))}
            </ul>
          </p>
          <StyledCompactTable
            dataSource={uploadResults}
            pagination={false}
            columns={[
              {
                title: '',
                dataIndex: 'result',
                key: 'result',
                width: 28,
                render: (result: ResultStatus) => (
                  <Tooltip title={t(statusTextMap[result])}>
                    <span
                      className={`block w-2 h-2 rounded-full ${statusColorMap[result]}`}
                    />
                  </Tooltip>
                ),
              },
              { title: t('Email'), dataIndex: 'email', key: 'email' },
              {
                title: t('User'),
                dataIndex: 'first_name',
                key: 'name',
                render: (a: any, record: any) =>
                  `${record.first_name ?? ''} ${
                    record.last_name ?? ''
                  }`.trim() || '-',
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportModal;
